/* eslint-disable max-lines */
import type { StateContext } from '@ngxs/store';
import { ColorGenerator, type Control, type DynamicFlatNode, type IChannel } from 'commons';
import { produce } from 'immer';
import { firstValueFrom } from 'rxjs';
import { multilane_config } from '../../../../components/dtis-multilane-chart/multi-lane.config';
import { isSameControlAs } from '../../../../components/dtis-multilane-viewer/multilane.utils';
import { CloseEditmode } from '../../../databrowser/lane-edit-mode-panel/editmode.actions';
import { db } from '../../../db';
import { LoadTestPhases } from '../../test-phase/test-phase.actions';
import {
  AddLaneConfig,
  type AddView,
  type DeleteView,
  GenerateEChartConfig,
  RefreshAllComments,
  type SelectedPageType,
  type SelectView,
  type SetStyles,
  type SetTableViewLaneFilter,
  type ToggleCommentMode,
  type ToggleTestPhasesVisibility,
  type UpdateView,
} from '../actions';
import type { MultiViewStateModel } from '../MultiViewStateModel';
import { SingleView } from '../SingleView';
import { filterNotMatchingId, getCurrentView, getViewIndex, getViewIndexById, LaneConfigHandler } from '.';

export namespace ViewActionHandler {
  type Context = StateContext<MultiViewStateModel>;

  /**
   * Adds a new view to the state.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the view to add.
   */
  export function addView(ctx: Context, { view }: AddView) {
    const newView =
      view ||
      (new SingleView({
        // owner: this.auth.handleAccount()?.localAccountId,
        label: `${(ctx.getState().views.length + 1).toString()}. View`,
      }) as any);
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.views.push({ ...newView });
      })
    );
    ctx.dispatch(new GenerateEChartConfig(newView.id));
  }

  /**
   * Clears the current view in the state.
   * @param ctx - The state context.
   */
  export function clearView(ctx: Context) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const i = getViewIndex(ctx);
        if (i < 0) {
          return;
        }
        draft.views[i].isLoading = [];
        draft.views[i].sensorTable = [];
        draft.views[i].lanes = [];
        draft.views[i].zoomState = [
          {
            start: 0,
            end: 100,
            startValue: undefined,
            endValue: undefined,
            axis: ['0'],
            dataZoomId: 'zoom_0_0',
          },
        ];
        draft.views[i].echartsConfig = { ...multilane_config };
      })
    );
    // Call LaneConfigHandler.add directly to avoid routing through the NGXS
    // action handler, which would call #captureHistory and create a spurious
    // intermediate history entry for the empty-lanes state.
    LaneConfigHandler.add(ctx, new AddLaneConfig());
    ctx.dispatch(CloseEditmode);
    ColorGenerator.setDefault();
  }

  /**
   * Sets the loading channels for the current view.
   * @param ctx - The state context.
   * @param loadingChannels - The channels that are currently loading.
   */
  export function setLoading(ctx: Context, loadingChannels: string[]) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const i = getViewIndex(ctx);
        if (i < 0) {
          return;
        }
        draft.views[i].isLoading = loadingChannels;
      })
    );
  }

  /**
   * Deletes a view from the state.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the ID of the view to delete.
   */
  export function deleteView(ctx: Context, { id }: DeleteView) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.views = draft.views.filter((view) => view.id !== id);
        if (draft.currentViewId === id) {
          draft.currentViewId = undefined;
        }
      })
    );
  }

  /**
   * Selects a view in the state.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the ID of the view to select.
   */
  export function selectView(ctx: Context, { id }: SelectView) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.currentViewId = id;
        const view = draft.views[draft.views.findIndex((v) => v.id === id)];
        if (!view) {
          return;
        }
        view.lastVisted = new Date();
        view.isLoading = [];
      })
    );
  }

  /**
   * Updates a view in the state.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the view to update.
   */
  export function updateView(ctx: Context, { view }: UpdateView) {
    const viewIndex = ctx.getState().views.findIndex((v) => v.id === view.id);
    if (viewIndex >= 0) {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          // biome-ignore lint/suspicious/noExplicitAny: immer draft typing is hard
          draft.views[viewIndex] = view as any;
        })
      );
    }
  }

  /**
   * Duplicates a view in the state.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the ID of the view to duplicate.
   */
  export function duplicateView(ctx: Context, { viewId }: any) {
    const view = ctx.getState().views.find((v) => v.id === viewId);
    if (!view) {
      return;
    }
    const newView = new SingleView({
      ...view,
      id: undefined,
      label: `${view.label} (copy)`,
    });
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.views.push(newView as any);
      })
    );
    ctx.dispatch(new GenerateEChartConfig(newView.id));
  }

  /**
   * Sets the styles for a target in the current view.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the styles and target to update.
   */
  export function setStyles(ctx: Context, { styles, target }: SetStyles) {
    if (!styles) {
      return;
    }
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const i = getViewIndex(ctx);
        if (i < 0) {
          return;
        }
        (draft.views[i].styles as any)[target] = {
          ...(draft.views[i].styles as any)[target],
          ...styles,
        };
      })
    );
  }

  /**
   * Removes a control from the current view.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the ID of the control to remove.
   */
  export async function removeControlFromCurrentView(ctx: StateContext<MultiViewStateModel>, { id }: Control) {
    const channels: DynamicFlatNode<IChannel>[] = (await db.channelInfo.toArray()) as DynamicFlatNode<IChannel>[];
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const viewIndex = getViewIndex(ctx);
        const view = draft.views[viewIndex];
        if (viewIndex >= 0) {
          view.lanes.forEach((lane) => {
            lane.controls = lane.controls.filter(filterNotMatchingId(id));
            for (const yAxis of lane.config?.yAxises ?? []) {
              if (yAxis.controls) {
                yAxis.controls = yAxis.controls.filter((cId) => cId !== id);
              }
            }
          });
          view.sensorTable = view.sensorTable.filter((control) => view.lanes.some(({ controls }) => controls.some(isSameControlAs(control))));

          // Remove comments only when all their mapped channels have been removed
          view.comments = view.comments.filter((comment) => comment.mappedChannels.some((channelId) => channelId !== id));
          // Remove comments only when none of their mapped channels are still active
          const remainingChannelIds = new Set(view.sensorTable.map((c) => c.id));
          view.comments = view.comments.filter((comment) => comment.mappedChannels.some((channelId) => remainingChannelIds.has(channelId)));
          view.lanes.forEach((_, laneIndex) => {
            LaneConfigHandler.updateTimestamp(view as SingleView, laneIndex, channels);
          });
        }
      })
    );
    ctx.dispatch(GenerateEChartConfig);
  }

  /**
   * Toggles the comment mode for the current view.
   * @param ctx - The state context.
   * @param action - The action payload containing the force state for the comment mode.
   */
  export function toggleCommentMode(ctx: StateContext<MultiViewStateModel>, action: ToggleCommentMode) {
    const i = getViewIndex(ctx);
    if (i < 0) {
      return;
    }

    const currentState = ctx.getState().views[i].commentModeEnabled;
    let willBeEnabled = false;
    let wasAlreadyEnabled = false;

    ctx.setState(
      produce(ctx.getState(), (draft) => {
        if (action.forceState) {
          if (action.forceState === 'on') {
            // Check if already enabled to avoid unnecessary refresh
            if (draft.views[i].commentModeEnabled) {
              wasAlreadyEnabled = true;
            } else {
              draft.views[i].commentModeEnabled = true;
              willBeEnabled = true;
            }
          }
          if (action.forceState === 'off') {
            draft.views[i].commentModeEnabled = false;
            draft.views[i].comments = [];
          }
        } else {
          draft.views[i].commentModeEnabled = !currentState;
          willBeEnabled = !currentState;
        }
      })
    );

    // Only load comments when enabling for the first time (not already enabled)
    if (willBeEnabled && !wasAlreadyEnabled) {
      ctx.dispatch(new RefreshAllComments());
    }
    ctx.dispatch(new GenerateEChartConfig());
  }

  /**
   * Handles the toggle test phases visibility action.
   * @param ctx - The state context.
   * @param action - The action payload containing the force state for test phases visibility.
   */
  export async function toggleTestPhasesVisibility(ctx: StateContext<MultiViewStateModel>, action: ToggleTestPhasesVisibility) {
    const i = getViewIndex(ctx);
    if (i < 0) {
      return;
    }

    const currentState = ctx.getState().views[i].testPhasesVisible;
    let willBeEnabled = false;

    ctx.setState(
      produce(ctx.getState(), (draft) => {
        if (action.forceState) {
          if (action.forceState === 'on') {
            draft.views[i].testPhasesVisible = true;
            willBeEnabled = true;
          }
          if (action.forceState === 'off') {
            draft.views[i].testPhasesVisible = false;
          }
        } else {
          draft.views[i].testPhasesVisible = !currentState;
          willBeEnabled = !currentState;
        }
      })
    );

    // Load test phases when enabling so they're available without opening the sidebar first
    if (willBeEnabled) {
      await firstValueFrom(ctx.dispatch(new LoadTestPhases()));
    }

    // Regenerate chart to reflect test phase visibility change
    ctx.dispatch(new GenerateEChartConfig());
  }

  /**
   * Handles the selected view action.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the view ID and page type.
   */
  export function handleSelectedView(ctx: Context, { viewId, pageType }: SelectedPageType) {
    if (pageType !== 'linechart') {
      ctx.dispatch(CloseEditmode);
    }

    ctx.setState(
      produce(ctx.getState(), (draft: any) => {
        const view = (viewId ? draft.views[getViewIndexById(ctx, viewId)] : getCurrentView(draft)) as SingleView;
        if (!view) {
          return;
        }
        view.pageType = pageType;
      })
    );
  }

  /**
   * Sets the table view lane filter for the current or specified view.
   * @param ctx - The state context.
   * @param param1 - The action payload containing the lane filter and optional view ID.
   */
  export function setTableViewLaneFilter(ctx: Context, action: SetTableViewLaneFilter) {
    ctx.setState(
      produce(ctx.getState(), (draft: any) => {
        const view = (action.viewId ? draft.views[getViewIndexById(ctx, action.viewId)] : getCurrentView(draft)) as SingleView;
        if (!view) {
          return;
        }
        view.tableViewLaneFilter = action.laneFilter;
      })
    );
  }

  export function resetLoading(ctx: StateContext<MultiViewStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const i = getViewIndex(ctx);
        if (i < 0) {
          return;
        }
        draft.views[i].isLoading = [];
      })
    );
  }
}
